import React, {useState, useMemo} from 'react';
import {
	Card,
	CardHeader,
	CardContent,
	Table,
	TableBody,
	TableRow,
	TableCell,
	Typography,
	Chip,
	Box,
	Button,
	Collapse,
	makeStyles,
} from '@material-ui/core';
import LaunchIcon from '@material-ui/icons/Launch';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import AcUnitIcon from '@material-ui/icons/AcUnit';
import moment from 'moment';

import PickupPointMap from './PickupPointMap';
import colors from '../../../../theme/colors';

const useStyles = makeStyles((theme) => ({
	table: {
		borderCollapse: 'separate',
		borderSpacing: 0,
		'& td, & th': {
			borderBottom: 'none',
			padding: '8px 12px',
			verticalAlign: 'top',
		},
		'& .MuiTableRow-root': {
			'&:hover': {
				backgroundColor: theme.palette.action.hover,
			},
		},
	},
	labelCell: {
		fontWeight: 'bold',
		width: '30%',
		whiteSpace: 'nowrap',
	},
	link: {
		display: 'inline-flex',
		alignItems: 'center',
		color: colors.blue,
		textDecoration: 'none',
		marginLeft: theme.spacing(1),
		cursor: 'pointer',
	},
	chip: {
		marginRight: theme.spacing(1),
		height: 22,
		fontSize: '0.72rem',
	},
	hoursRow: {
		display: 'flex',
		justifyContent: 'space-between',
		padding: '2px 0',
		fontSize: '0.82rem',
	},
	hoursToday: {
		fontWeight: 'bold',
		color: colors.darkBold,
	},
	hoursToggle: {
		textTransform: 'none',
		padding: 0,
		minWidth: 0,
		fontSize: '0.75rem',
	},
	mapWrapper: {
		marginTop: theme.spacing(2),
	},
	noCoords: {
		marginTop: theme.spacing(2),
		padding: theme.spacing(2),
		backgroundColor: colors.lightMediumColor,
		borderRadius: 4,
		textAlign: 'center',
	},
}));

const hasCoordinates = (point) =>
	point &&
	point.latitude !== null &&
	point.latitude !== undefined &&
	point.longitude !== null &&
	point.longitude !== undefined &&
	!Number.isNaN(Number(point.latitude)) &&
	!Number.isNaN(Number(point.longitude));

const PickupPointCard = ({details}) => {
	const classes = useStyles();
	const [showAllHours, setShowAllHours] = useState(false);

	const pickupPoint = details?.pickup_point;
	const deliveryCompany = pickupPoint?.delivery_company;

	const openingDays = useMemo(
		() => pickupPoint?.opening_hours?.days || [],
		[pickupPoint]
	);
	const todayName = moment().format('dddd');
	const todayHours = useMemo(
		() => openingDays.find((d) => d.day === todayName),
		[openingDays, todayName]
	);

	// No pickup point on this order — render nothing.
	if (!pickupPoint) return null;

	const showMap = hasCoordinates(pickupPoint);
	const directionsUrl = showMap
		? `https://www.google.com/maps/search/?api=1&query=${pickupPoint.latitude},${pickupPoint.longitude}`
		: null;

	return (
		<Card className="mb-3">
			<CardHeader
				title={
					<Box display="flex" alignItems="center" flexWrap="wrap">
						<Typography variant="h6"><b>PICKUP POINT</b></Typography>
						<Box ml={2} display="flex" alignItems="center">
							<Chip
								className={classes.chip}
								size="small"
								label={pickupPoint.is_active ? 'Active' : 'Inactive'}
								style={{
									backgroundColor: pickupPoint.is_active ? colors.primary : colors.darkLight,
									color: colors.white,
								}}
							/>
							{pickupPoint.has_cool_storage && (
								<Chip
									className={classes.chip}
									size="small"
									icon={<AcUnitIcon style={{fontSize: 14, color: colors.white}}/>}
									label="Cool storage"
									style={{backgroundColor: colors.blue, color: colors.white}}
								/>
							)}
						</Box>
					</Box>
				}
			/>
			<CardContent>
				<Table className={classes.table}>
					<TableBody>
						<TableRow>
							<TableCell className={classes.labelCell}>Name</TableCell>
							<TableCell>{pickupPoint.name || '-'}</TableCell>
						</TableRow>
						<TableRow>
							<TableCell className={classes.labelCell}>Delivery company</TableCell>
							<TableCell>
								{deliveryCompany
									? `${deliveryCompany.name}${deliveryCompany.code ? ` (${deliveryCompany.code})` : ''}`
									: '-'}
							</TableCell>
						</TableRow>
						<TableRow>
							<TableCell className={classes.labelCell}>Address</TableCell>
							<TableCell>
								{pickupPoint.address || '-'}
								{directionsUrl && (
									<a
										href={directionsUrl}
										target="_blank"
										rel="noopener noreferrer"
										className={classes.link}
									>
										Directions <LaunchIcon style={{fontSize: 14, marginLeft: 2}}/>
									</a>
								)}
							</TableCell>
						</TableRow>
						<TableRow>
							<TableCell className={classes.labelCell}>Phone</TableCell>
							<TableCell>
								{pickupPoint.phone || '-'}
								{deliveryCompany?.contact_phone &&
									deliveryCompany.contact_phone !== pickupPoint.phone && (
										<Typography variant="caption" style={{color: colors.darkRegular, display: 'block'}}>
											Company: {deliveryCompany.contact_phone}
										</Typography>
									)}
							</TableCell>
						</TableRow>
						{openingDays.length > 0 && (
							<TableRow>
								<TableCell className={classes.labelCell}>Opening hours</TableCell>
								<TableCell>
									<Box className={`${classes.hoursRow} ${classes.hoursToday}`}>
										<span>{todayHours ? `${todayHours.day} (today)` : `${todayName} (today)`}</span>
										<span>{todayHours ? `${todayHours.open} - ${todayHours.close}` : 'Closed'}</span>
									</Box>
									<Collapse in={showAllHours}>
										{openingDays
											.filter((d) => d.day !== todayName)
											.map((d) => (
												<Box key={d.day} className={classes.hoursRow}>
													<span>{d.day}</span>
													<span>{d.open && d.close ? `${d.open} - ${d.close}` : 'Closed'}</span>
												</Box>
											))}
									</Collapse>
									<Button
										color="primary"
										className={classes.hoursToggle}
										onClick={() => setShowAllHours((prev) => !prev)}
										endIcon={showAllHours
											? <ExpandLessIcon style={{fontSize: 16}}/>
											: <ExpandMoreIcon style={{fontSize: 16}}/>}
									>
										{showAllHours ? 'Hide full week' : 'Show full week'}
									</Button>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>

				{showMap ? (
					<Box className={classes.mapWrapper}>
						<PickupPointMap
							latitude={pickupPoint.latitude}
							longitude={pickupPoint.longitude}
							name={pickupPoint.name}
							address={pickupPoint.address}
						/>
					</Box>
				) : (
					<Box className={classes.noCoords}>
						<Typography variant="body2" style={{color: colors.darkRegular}}>
							No coordinates available for this pickup point.
						</Typography>
					</Box>
				)}
			</CardContent>
		</Card>
	);
};

export default PickupPointCard;
